/**
 * @summary - Component that Provides User Department Listing Page
 * @author - Durai Murugan B S
 * @since
 */

import React, { useState, useEffect } from "react";
import { Box, Stack } from "@mui/material";
import CustomAgGrid from "../../components/commoncomponents/CustomAgGrid";
import CustomDialog from "../../components/commoncomponents/CustomDialog";
import { useNavigate } from "react-router-dom";
import { DefaultCard } from "../../assets/styled-components/card";
import { getAuthToken, getFacilityId } from "../../utility/tokenUtility";
import axios from "axios";
import CreateNewButton from "../../components/commoncomponents/CreateNewButton";
import RefreshExportButtons from "../commoncomponents/RefreshExportButtons";
import { MasterGraph } from "../../utility/apiURLS";
import { dialogContent } from "../../utility/message";
import PageTitle from "../commoncomponents/PageTitle";
import SectionTitle from "../commoncomponents/SectionTitle";
import CloseButton from "../commoncomponents/CloseButton";
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';

const Userdepartmentlist = () => {
  const [rows, setRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const navigate = useNavigate();
  const [gridApi, setGridApi] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportCount, setExportCount] = useState(0)

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "ENGM007");

  const columns = [
    { field: "departmentcode", headerName: "Department Code", width: 180 },
    {
      field: "departmentdesc",
      headerName: "Department Description",
      width: 150,
    },
  ];

  useEffect(() => {
    fetchUserDepartments();

    
  }, [filterModel, sortModel, pageIndex, pageSize]);

  const fetchUserDepartments = async () => {
    setRows([]);
    setIsLoading(true);
    try {
      const token = getAuthToken("id_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const facilityId = getFacilityId("facilityId");
      const url = `${MasterGraph}/api/GraphUserDepartmentDetails/GetUserDepartmentDetailsByFacilityId/${facilityId}?page=${pageIndex}&size=${pageSize}&filterOptions=${JSON.stringify(
        filterModel
      )}&sortOptions=${JSON.stringify(sortModel)}`;

      const response = await axios.get(url, config);
      setRowCount(response.data.totalCount);
      setExportCount(response.data.totalCount)

      if (response.data && Array.isArray(response.data.items)) {
        const formattedData = response.data.items.map((item) => ({
          ...item,
          id: item.id, // Ensure each row has a unique 'id' field for editing/deleting
          departmentcode: item.departmentcode,
          departmentdesc: item.departmentdesc,
        }));
        setRows(formattedData);
      } else {
        // console.error("Unexpected response format:", response.data);
      }
    } catch (error) {
      //  console.error("Error fetching user departments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExportdata = async () => {
    setIsLoading(true);
    try {
      const token = getAuthToken("id_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const facilityId = getFacilityId("facilityId");

      const exportUrl = `${MasterGraph}/api/CommonExport/CommonExportData/${facilityId}/Department?filterOptions=${JSON.stringify(
        filterModel
      )}&sortOptions=${JSON.stringify(sortModel)}`;
      const exportResponse = await axios.get(exportUrl, config);
      setExportData(exportResponse.data.masuserdepartmentdetails)

    } catch (error) {
      //  console.error("Error fetching user departments:", error);
    } finally {
      setIsLoading(false);
    }
  }


  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
    
    
  };



  const confirmDelete = async () => {
    try {
      const token = getAuthToken("id_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      if (!rowToDelete || !rowToDelete.id) {
        //  console.error("Row to delete or its id is undefined");
        return;
      }

      const response = await axios.delete(
        `${MasterGraph}/api/masuserdepartmentdetails/${rowToDelete.id}`,
        config
      );

      if (response.status === 204) {
        setSuccessDialogOpen(true);
      }

      const updatedRows = rows.filter((row) => row.id !== rowToDelete.id);
      setRows(updatedRows);
    } catch (error) {
      if (error.response && error.response.status === 400) {
        setErrorMessage(
          "Record cannot be deleted as it is referred in another record."
        );
      } else {
        setErrorMessage(
          "An error occurred while deleting the user department. Please try again."
        );
      }
    } finally {
      setDeleteDialogOpen(false);
      setRowToDelete(null);
      fetchUserDepartments();
    }
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    
  };

  const handleCreateUserDepartment = () => {
    navigate("register");
  };

  const handleEdit = (rowData) => {
    navigate("register", { state: { ...rowData, readOnly: false } });
  };

  return (
    <>
      <Box mt={{ xs: 1, sm: 2 }}>
        <PageTitle title="User Department" />
        <DefaultCard>
          <Box
            mb={1}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <SectionTitle title="User Department List" />
            <Stack direction={"row"} spacing={1.5}>
              {access?.canCreate && <CreateNewButton onClick={handleCreateUserDepartment} />}
              <RefreshExportButtons
                exportFunction={fetchExportdata}
                exportData={exportData}
                refData={gridApi}
                handleRefresh={handleRefresh}
                title="User Department"
              />
            </Stack>
          </Box>
          <CustomAgGrid
            rows={rows}
            columns={columns}
            handleEdit={access?.canEdit && handleEdit}
            handleDelete={access?.canDelete &&handleDelete}
            // handleView={handleView}
            ref={gridApi}
            setGridApi={setGridApi}
            pagination={false}
            confirmBtn="Ok"
            isLoading={isLoading}
            setFilterModel={setFilterModel}
            setSortModel={setSortModel}
            totalCount={rowCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPageIndex={setPageIndex}
            setPageSize={setPageSize}
          />
        </DefaultCard>
      </Box>
      <CustomDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        content={dialogContent.confirmDelete}
        confirmBtn="Yes"
        cancelBtn="No"
      />
      <CustomDialog
        open={successDialogOpen}
        onClose={() => handleSuccessDialogClose(false)}
        content={dialogContent.deleted}
        confirmBtn="Ok"
        onConfirm={() => handleSuccessDialogClose(false)}
      />
      <CustomDialog
        open={!!errorMessage}
        onClose={() => setErrorMessage("")}
        onConfirm={() => setErrorMessage("")}
        content={errorMessage}
        confirmBtn="oK"
      />
      <CloseButton />
    </>
  );
};

export default Userdepartmentlist;
